import { useCallback, useEffect, useState } from "react";
import { BellRing, BellOff, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { apiGet, apiPost } from "@/lib/api";

const b64ToUint8 = (b64) => {
  const pad = "=".repeat((4 - (b64.length % 4)) % 4);
  const raw = atob((b64 + pad).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
};

export const PushPanel = () => {
  const supported =
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window;

  const [enabled, setEnabled] = useState(null);
  const [busy, setBusy] = useState(false);
  const [generalPushEnabled, setGeneralPushEnabled] = useState(true);
  const [generalPushBusy, setGeneralPushBusy] = useState(false);

  const check = useCallback(async () => {
    if (!supported) return;

    try {
      const reg = await navigator.serviceWorker.getRegistration("/sw.js");
      const sub = reg && (await reg.pushManager.getSubscription());
      setEnabled(!!sub);
    } catch {
      setEnabled(false);
    }
  }, [supported]);

  useEffect(() => {
    check();
  }, [check]);

  useEffect(() => {
    const loadGeneralPush = async () => {
      try {
        const { data } = await apiGet("/general-push/status");
        setGeneralPushEnabled(data?.enabled !== false);
      } catch (err) {
        console.error("General Push status error:", err);
        setGeneralPushEnabled(true);
      }
    };

    loadGeneralPush();
  }, []);

  const enable = async () => {
    setBusy(true);

    try {
      if (!window.isSecureContext) {
        throw new Error("Push Notification membutuhkan HTTPS.");
      }

      const permission = await Notification.requestPermission();

      if (permission !== "granted") {
        throw new Error(
          "Izin notifikasi ditolak. Aktifkan notifikasi NakedChart di pengaturan perangkat."
        );
      }

      const reg = await navigator.serviceWorker.register("/sw.js");
      await navigator.serviceWorker.ready;

      const { data } = await apiGet("/push/vapid-key");

      let sub = await reg.pushManager.getSubscription();

      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: b64ToUint8(data.key),
        });
      }

      await apiPost("/push/subscribe", {
        subscription: sub.toJSON(),
      });

      setEnabled(true);

      toast.success("Notifikasi HP berhasil diaktifkan");

      // Kirim satu notifikasi uji.
      try {
        await apiPost("/push/test", {});
      } catch (testError) {
        console.error("Push test error:", testError);
      }
    } catch (err) {
      console.error("Push enable error:", err);
      toast.error(
        err?.response?.data?.detail ||
          err?.message ||
          "Gagal mengaktifkan notifikasi"
      );
    } finally {
      setBusy(false);
    }
  };

  const toggleGeneralPush = async () => {
    setGeneralPushBusy(true);
    const next = !generalPushEnabled;

    try {
      await apiPost("/general-push/settings", { enabled: next });
      setGeneralPushEnabled(next);
      toast.success(next ? "General Push Notification diaktifkan" : "General Push Notification dimatikan");
    } catch (err) {
      console.error("General Push toggle error:", err);
      toast.error(err?.response?.data?.detail || "Gagal mengubah General Push Notification");
    } finally {
      setGeneralPushBusy(false);
    }
  };

  const disable = async () => {
    setBusy(true);

    try {
      const reg = await navigator.serviceWorker.getRegistration("/sw.js");
      const sub = reg && (await reg.pushManager.getSubscription());

      if (sub) {
        await apiPost("/push/unsubscribe", {
          endpoint: sub.endpoint,
        }).catch(() => {});

        await sub.unsubscribe();
      }

      setEnabled(false);
      toast.success("Notifikasi HP dimatikan");
    } catch (err) {
      console.error("Push disable error:", err);
      toast.error(
        err?.response?.data?.detail || "Gagal mematikan notifikasi"
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="border border-white/10 bg-[#0d1219] p-4"
      data-testid="push-panel"
    >
      <p className="flex items-center gap-2 font-mono text-[9px] uppercase tracking-[0.25em] text-mute-dim">
        <Smartphone size={12} className="text-buy" />
        Notifikasi HP / Browser
      </p>

      <p className="mt-2 font-mono text-[10px] leading-relaxed text-mute">
        Terima notifikasi sinyal baru, sinyal ditutup, dan peringatan berita
        langsung di perangkat ini — tanpa perlu Telegram.
      </p>

      {!supported ? (
        <p
          className="mt-3 border border-warn/30 bg-warn/5 px-3 py-2 font-mono text-[10px] leading-relaxed text-warn"
          data-testid="push-unsupported"
        >
          Browser ini belum mendukung push. Di iPhone: buka menu Bagikan →
          "Tambahkan ke Layar Utama", lalu buka aplikasi dari ikon tersebut
          untuk mengaktifkan notifikasi.
        </p>
      ) : enabled ? (
        <button
          onClick={disable}
          disabled={busy}
          data-testid="push-disable"
          className="mt-3 flex h-9 items-center gap-2 border border-sell/50 bg-sell/10 px-4 font-mono text-[11px] font-bold uppercase tracking-wider text-sell transition-colors hover:bg-sell hover:text-white disabled:opacity-50"
        >
          <BellOff size={13} />
          Matikan Notifikasi
        </button>
      ) : (
        <button
          onClick={enable}
          disabled={busy || enabled === null}
          data-testid="push-enable"
          className="mt-3 flex h-9 items-center gap-2 border border-buy bg-buy/10 px-4 font-mono text-[11px] font-bold uppercase tracking-wider text-buy transition-colors hover:bg-buy hover:text-ink disabled:opacity-50"
        >
          <BellRing size={13} />
          Aktifkan Notifikasi
        </button>
      )}
      <div
        className="mt-5 border-t border-white/10 pt-4"
        data-testid="general-push-panel"
      >
        <p className="flex items-center gap-2 font-mono text-[9px] uppercase tracking-[0.25em] text-mute-dim">
          <BellRing size={12} className="text-buy" />
          General Push Notifikasi
        </p>

        <p className="mt-2 font-mono text-[10px] leading-relaxed text-mute">
          Terima pemberitahuan umum seperti Support, akun baru, pembayaran,
          dan informasi sistem langsung di perangkat ini.
        </p>

        <button
          onClick={toggleGeneralPush}
          disabled={generalPushBusy}
          data-testid="general-push-toggle"
          className={`mt-3 flex h-9 items-center gap-2 border px-4 font-mono text-[11px] font-bold uppercase tracking-wider transition-colors disabled:opacity-50 ${
            generalPushEnabled
              ? "border-buy bg-buy/10 text-buy hover:bg-buy hover:text-ink"
              : "border-sell/50 bg-sell/10 text-sell hover:bg-sell hover:text-white"
          }`}
        >
          {generalPushEnabled ? (
            <>
              <BellRing size={13} />
              General Push Aktif
            </>
          ) : (
            <>
              <BellOff size={13} />
              General Push Mati
            </>
          )}
        </button>
      </div>
    </div>
  );
};